import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { X } from 'lucide-react';
import { api } from '../../utils/api';
import { UserRole, MessageUser } from '@/types/message';
import { useAuth } from '@/contexts/AuthContext';
import { createPortal } from 'react-dom';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  overflow: hidden;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s ease-in-out, visibility 0.2s ease-in-out;

  &.visible {
    opacity: 1;
    visibility: visible;
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  padding: 24px;
  width: 100%;
  max-width: 500px;
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  z-index: 10000;
  will-change: transform;
  pointer-events: auto;
  transition: transform 0.2s ease-out;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  padding: 4px;
  border-radius: 4px;
  
  &:hover {
    color: #374151;
    background: #f3f4f6;
  }
`;

const Title = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 16px;
  padding-right: 32px;
`;

const RoleBadge = styled.span<{ role: UserRole }>`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  margin-left: 8px;
  background-color: ${({ role }) => {
    switch (role) {
      case 'teacher':
        return '#e0e7ff';
      case 'student':
        return '#f3e8ff';
      case 'parent':
        return '#fef3c7';
    }
  }};
  color: ${({ role }) => {
    switch (role) {
      case 'teacher':
        return '#4338ca';
      case 'student':
        return '#7c3aed';
      case 'parent':
        return '#b45309';
    }
  }};
`;

const MessageContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  margin-bottom: 16px;
  padding-right: 8px;
  height: calc(80vh - 200px);
  min-height: 200px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 3px;
  }
`;

const MessageBubble = styled.div<{ isSender: boolean }>`
  padding: 8px 12px;
  border-radius: 12px;
  max-width: 80%;
  align-self: ${({ isSender }) => isSender ? 'flex-end' : 'flex-start'};
  background-color: ${({ isSender }) => isSender ? '#e3f2fd' : '#f5f5f5'};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  position: relative;
  word-wrap: break-word;
  white-space: pre-wrap;
`;

const MessageText = styled.div`
  font-size: 0.875rem;
  line-height: 1.4;
  word-break: break-word;
  white-space: pre-wrap;
`;

const MessageTime = styled.div`
  font-size: 0.75rem;
  color: #666;
  margin-top: 4px;
  text-align: right;
  position: relative;
`;

const MessageInput = styled.textarea`
  width: 100%;
  min-height: 80px;
  max-height: 120px;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  margin-bottom: 16px;
  resize: vertical;
  font-size: 0.875rem;
  line-height: 1.4;
  position: relative;
  overflow-y: auto;
  
  &:focus {
    outline: none;
    border-color: #7c3aed;
    box-shadow: 0 0 0 2px rgba(124, 58, 237, 0.1);
  }
`;

const SendButton = styled.button`
  background: #7c3aed;
  color: white;
  padding: 8px 16px;
  border-radius: 6px;
  border: none;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  position: relative;
  
  &:hover {
    background: #6d28d9;
  }
  
  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #dc2626;
  margin-bottom: 16px;
  font-size: 0.875rem;
  padding: 8px;
  background-color: #fee2e2;
  border-radius: 4px;
  position: relative;
`;

interface Message {
  _id: string;
  content: string;
  sender: {
    id: string;
  };
  createdAt: string;
}

interface MessageModalProps {
  isOpen: boolean;
  onClose: () => void;
  sender: MessageUser;
  receiver: MessageUser;
  conversationId?: string;
}

const MessageModal: React.FC<MessageModalProps> = ({
  isOpen,
  onClose,
  sender,
  receiver,
  conversationId
}) => {
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const { token } = useAuth();
  const modalRef = React.useRef<HTMLDivElement>(null);
  const overlayRef = React.useRef<HTMLDivElement>(null);
  const messageContainerRef = React.useRef<HTMLDivElement>(null);

  // Handle modal visibility
  useEffect(() => {
    if (isOpen && overlayRef.current) {
      requestAnimationFrame(() => {
        if (overlayRef.current) {
          overlayRef.current.classList.add('visible');
        }
      });
    } else if (overlayRef.current) {
      overlayRef.current.classList.remove('visible');
    }
  }, [isOpen]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (messageContainerRef.current) {
      messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
    }
  }, [messages]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node) && 
          overlayRef.current && overlayRef.current === event.target) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    let pollInterval: NodeJS.Timeout;
    let isPolling = true;

    const fetchMessages = async () => {
      if (!isPolling) return;
      
      try {
        const response = await api.get(`/messages/${conversationId}`);

        const data = response.data;
        if (data.success && Array.isArray(data.data)) {
          const sortedMessages = data.data.sort((a: Message, b: Message) => 
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          );
          setMessages(sortedMessages);
        } else {
          console.error('Error: Expected array of messages');
          setError('Failed to load messages');
        }
      } catch (err) {
        console.error('Error fetching messages:', err);
        setError('Failed to load messages');
      }
    };

    if (isOpen && token && conversationId) {
      fetchMessages();
      pollInterval = setInterval(fetchMessages, 3000); // Poll every 3 seconds
    }

    return () => {
      isPolling = false;
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [isOpen, conversationId, token]);

  useEffect(() => {
    if (isOpen && conversationId && sender.id && token) {
      console.log('Mark as read: sender.id =', sender.id, 'sender.name =', sender.name);
      api.post('/messages/mark-conversation-as-read', { conversationId, userId: sender.id })
      .catch(err => {
        console.error('Failed to mark conversation as read', err);
      });
    }
  }, [isOpen, conversationId, sender.id, token]);

  const handleSend = async () => {
    if (!message.trim() || !token) return;

    setIsSending(true);
    setError('');

    try {
      const messageData = {
        conversationId,
        senderId: sender.id,
        receiverId: receiver.id,
        text: message.trim(),
        senderRole: sender.role,
        receiverRole: receiver.role,
        senderName: sender.name,
        receiverName: receiver.name
      };

      const response = await api.post('/messages/send', messageData);

      if (response.data?.success) {
        setMessages(prev => [...prev, response.data.data]);
        setMessage('');
      } else {
        throw new Error(response.data?.error || 'Failed to send message');
      }
    } catch (err: any) {
      console.error('Error sending message:', err);
      setError(err.response?.data?.error || err.message || 'Failed to send message. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    (
      <ModalOverlay ref={overlayRef}>
        <ModalContent ref={modalRef}>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
          
          <Title>
            Conversation with {receiver.name}
            <RoleBadge role={receiver.role}>
              {receiver.role.charAt(0).toUpperCase() + receiver.role.slice(1)}
            </RoleBadge>
          </Title>

          <MessageContainer ref={messageContainerRef}>
            {messages.map(msg => (
              <MessageBubble key={msg._id} isSender={msg.sender?.id === sender.id}>
                <MessageText>{msg.content}</MessageText>
                <MessageTime>
                  {new Date(msg.createdAt).toLocaleTimeString()}
                </MessageTime>
              </MessageBubble>
            ))}
          </MessageContainer>
          
          <MessageInput
            value={message}
            onChange={e => setMessage(e.target.value)}
            placeholder="Type your message here..."
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
          />
          
          {error && <ErrorMessage>{error}</ErrorMessage>}
          
          <SendButton
            onClick={handleSend}
            disabled={!message.trim() || isSending}
          >
            {isSending ? 'Sending...' : 'Send Message'}
          </SendButton>
        </ModalContent>
      </ModalOverlay>
    ),
    document.body
  );
};

export default MessageModal; 